import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Six-vertex radar of the read, one spoke per side of the person.
 *
 * Vertices map 1:1 to what the scoring engine computes: interests,
 * traits, aptitudes, values, motivators, readiness. The seventh
 * backend layer ("situation") is intake context, not a scored side.
 *
 * Honest empty state: a side with no real signal sits at the centre
 * and reads "not enough heard yet". Zero is the absence of a score,
 * not a low score, so padded zero constructs are ignored when we
 * average the strongest signals. UI copy is plain language, never a
 * framework name (invariant 8). Zero em dashes or en dashes.
 */

const LAYER_ORDER = [
  { key: "interests",   label: "What pulls you",        hue: "L4",
    subtitle: "the shape of what you keep coming back to" },
  { key: "traits",      label: "How you show up",       hue: "L1",
    subtitle: "your steady-state rhythms and reflexes" },
  { key: "aptitudes",   label: "Where thinking lands",  hue: "L2",
    subtitle: "how your reasoning tends to move" },
  { key: "values",      label: "What you protect",      hue: "L5",
    subtitle: "the ground you will not give up" },
  { key: "motivators",  label: "What keeps you moving", hue: "L6",
    subtitle: "what pulls the effort out of you" },
  { key: "readiness",   label: "Ready to move",         hue: "L7",
    subtitle: "a composite of what carries a plan through" },
];

function matchLayer(layerName) {
  const k = (layerName || "").toString().trim().toLowerCase();
  if (!k) return null;
  return LAYER_ORDER.find((L) => L.key === k) || null;
}

// Average only the constructs that carry real signal. Unanswered
// constructs are padded with a literal 0.0 by the backend, so a zero
// means "not heard", not "scored low". All-zero (or empty) reads as
// null, the honest no-signal state.
function layerScore(layer) {
  const tc = layer && Array.isArray(layer.top_constructs) ? layer.top_constructs : null;
  if (!tc || tc.length === 0) return null;
  const scored = tc.map((c) => Number(c.score) || 0).filter((s) => s > 0);
  if (scored.length === 0) return null;
  return scored.reduce((a, b) => a + b, 0) / scored.length;
}

function citedCount(layer) {
  const tc = layer && Array.isArray(layer.top_constructs) ? layer.top_constructs : null;
  if (!tc) return 0;
  return tc.filter((c) => (Number(c.score) || 0) > 0).length;
}

function toneFor(score) {
  if (score === null || score === undefined) return "not heard yet";
  const pct = Math.round((score / 5) * 100);
  if (pct >= 70) return "comes through strong";
  if (pct >= 45) return "comes through steady";
  return "comes through quietly";
}

// Split a label into at most two balanced lines so long ones never
// spill past the plot edge.
function wrapLabel(text) {
  const words = text.split(" ");
  if (words.length <= 2) return [text];
  let best = 0, bestDiff = Infinity;
  const full = text.length;
  for (let i = 1; i < words.length; i++) {
    const left = words.slice(0, i).join(" ").length;
    const diff = Math.abs(left - (full - left));
    if (diff < bestDiff) { bestDiff = diff; best = i; }
  }
  return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
}

export function RadarChart({ layers = [], size = 300 }) {
  const reduce = useReducedMotion();
  const [hover, setHover] = useState(null);

  const PAD = 96;                 // room around the plot for labels
  const W = size + PAD * 2;       // full square canvas
  const cx = W / 2;
  const cy = W / 2;
  const R = size * 0.42;          // plot radius

  const points = useMemo(() => {
    return LAYER_ORDER.map((L, i) => {
      const angle = (Math.PI * 2 * i) / LAYER_ORDER.length - Math.PI / 2;
      const layer = layers.find((l) => matchLayer(l.layer)?.key === L.key) || null;
      const raw = layerScore(layer);
      const s = raw == null ? 0 : Math.max(0, Math.min(5, raw));
      const r = (s / 5) * R;
      const cos = Math.cos(angle), sin = Math.sin(angle);
      const labelR = R + 24;
      return {
        L,
        raw,
        score: s,
        hasSignal: raw != null,
        x: cx + cos * r,
        y: cy + sin * r,
        axisX: cx + cos * R,
        axisY: cy + sin * R,
        labelX: cx + cos * labelR,
        labelY: cy + sin * labelR,
        anchor: cos > 0.3 ? "start" : cos < -0.3 ? "end" : "middle",
        dyBase: sin > 0.3 ? 10 : sin < -0.3 ? -4 : 3,
      };
    });
  }, [layers, cx, cy, R]);

  const anySignal = points.some((p) => p.hasSignal);
  const poly = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const rings = [1, 2, 3, 4, 5];

  return (
    <div className="w-full" data-testid="radar">
      <div
        className="rounded-2xl relative overflow-hidden"
        style={{
          background: "radial-gradient(120% 120% at 50% 30%, hsl(var(--klareet-canvas)) 0%, hsl(var(--klareet-canvas-2)) 100%)",
          border: "1px solid hsl(var(--klareet-line-soft))",
        }}
      >
        <svg viewBox={`0 0 ${W} ${W}`} className="w-full h-auto block" aria-label="your six sides">
          <defs>
            <radialGradient id="radar-fill" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="hsl(var(--klareet-teal))" stopOpacity="0.34" />
              <stop offset="100%" stopColor="hsl(var(--klareet-blue))" stopOpacity="0.12" />
            </radialGradient>
          </defs>

          {/* concentric rings */}
          {rings.map((ring) => (
            <polygon
              key={ring}
              fill={ring === 5 ? "hsl(var(--klareet-canvas))" : "none"}
              fillOpacity={ring === 5 ? 0.4 : 0}
              stroke="hsl(var(--klareet-line))"
              strokeOpacity={ring === 5 ? 0.5 : 0.28}
              strokeWidth={0.8}
              points={LAYER_ORDER.map((_, j) => {
                const a = (Math.PI * 2 * j) / LAYER_ORDER.length - Math.PI / 2;
                const rr = R * (ring / 5);
                return `${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`;
              }).join(" ")}
            />
          ))}

          {/* scale marks on the top spoke: 1..5 */}
          {rings.map((ring) => (
            <text
              key={`sc-${ring}`}
              x={cx + 6}
              y={cy - (R * ring) / 5 + 3}
              style={{ fontSize: 9, fontFamily: "var(--font-mono, monospace)" }}
              fill="hsl(var(--klareet-muted))"
              fillOpacity={0.6}
            >
              {ring}
            </text>
          ))}

          {/* spokes */}
          {points.map((p) => (
            <line key={`ax-${p.L.key}`} x1={cx} y1={cy} x2={p.axisX} y2={p.axisY}
              stroke="hsl(var(--klareet-line))" strokeOpacity={0.3} strokeWidth={0.8} />
          ))}

          {/* the shape */}
          {anySignal && (
            <motion.g
              style={{ transformOrigin: `${cx}px ${cy}px` }}
              initial={reduce ? false : { scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 120, damping: 16 }}
            >
              <polygon points={poly} fill="url(#radar-fill)" stroke="hsl(var(--klareet-teal))" strokeWidth={2} strokeLinejoin="round" />
            </motion.g>
          )}

          {/* vertices */}
          {points.map((p, i) => (
            <g key={p.L.key}
              onMouseEnter={() => setHover(p.L.key)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "default" }}>
              {p.hasSignal && hover === p.L.key && (
                <circle cx={p.x} cy={p.y} r={11} fill={`hsl(var(--k-${p.L.hue}))`} fillOpacity={0.2} />
              )}
              <motion.circle
                cx={p.x} cy={p.y}
                r={p.hasSignal ? 5 : 3}
                fill={p.hasSignal ? `hsl(var(--k-${p.L.hue}))` : "hsl(var(--klareet-canvas))"}
                stroke={p.hasSignal ? "white" : "hsl(var(--klareet-line))"}
                strokeWidth={p.hasSignal ? 2 : 1.4}
                initial={reduce ? false : { scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.25 + i * 0.05, type: "spring", stiffness: 260, damping: 18 }}
                data-testid={`radar-dot-${p.L.key}`}
              >
                <title>{p.L.label}: {p.hasSignal ? p.score.toFixed(1) : "not heard yet"}</title>
              </motion.circle>
            </g>
          ))}

          {/* labels, side-aware and wrapped */}
          {points.map((p) => {
            const lines = wrapLabel(p.L.label);
            const on = hover === p.L.key;
            return (
              <text key={`lb-${p.L.key}`} x={p.labelX} y={p.labelY + p.dyBase}
                textAnchor={p.anchor}
                style={{ fontSize: 11.5, fontWeight: on ? 700 : 500 }}
                fill={on ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-muted))"}>
                {lines.map((ln, k) => (
                  <tspan key={k} x={p.labelX} dy={k === 0 ? 0 : 13}>{ln}</tspan>
                ))}
                <tspan x={p.labelX} dy={14}
                  style={{ fontSize: 10, fontFamily: "var(--font-mono, monospace)" }}
                  fill={p.hasSignal ? `hsl(var(--k-${p.L.hue}))` : "hsl(var(--klareet-muted))"}>
                  {p.hasSignal ? p.score.toFixed(1) : "not heard yet"}
                </tspan>
              </text>
            );
          })}

          {!anySignal && (
            <text x={cx} y={cy + 4} textAnchor="middle" style={{ fontSize: 12 }} fill="hsl(var(--klareet-muted))">
              not enough heard yet
            </text>
          )}
        </svg>
      </div>

      {/* how to read */}
      <div className="mt-3 flex items-start gap-2 px-1" data-testid="radar-how-to-read">
        <span className="k-quiet !text-xs" style={{ lineHeight: 1.5 }}>
          <span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>How to read this: </span>
          each spoke is one side of you. The further a point sits from the centre, the stronger and clearer that side came through, on a 0 to 5 scale. A point resting near the centre just means we have not heard enough on it yet.
        </span>
      </div>
    </div>
  );
}

export function SignalLegend({ layers = [] }) {
  return (
    <ul className="grid grid-cols-2 gap-2" data-testid="signal-legend">
      {LAYER_ORDER.map((L) => {
        const layer = layers.find((l) => matchLayer(l.layer)?.key === L.key) || null;
        const s = layerScore(layer);
        const tone = toneFor(s);
        const cited = citedCount(layer);
        return (
          <li
            key={L.key}
            className="flex items-center gap-3 p-2.5 rounded-lg"
            style={{
              border: "1px solid hsl(var(--klareet-line-soft))",
              background: `linear-gradient(90deg, hsl(var(--k-${L.hue}) / 0.08), transparent 65%)`,
            }}
            data-testid={`legend-${L.key}`}
          >
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ background: `hsl(var(--k-${L.hue}))`, boxShadow: `0 0 0 3px hsl(var(--k-${L.hue}) / 0.18)` }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{L.label}</div>
              <div className="k-quiet !text-xs" data-testid={`legend-${L.key}-tone`}>
                {tone}
                {cited > 0 && (
                  <span> · {cited} {cited === 1 ? "signal from you" : "signals from you"}</span>
                )}
              </div>
            </div>
            <div className="klareet-mono text-sm" style={{ color: s == null ? "hsl(var(--klareet-muted))" : `hsl(var(--k-${L.hue}))` }}>
              {s == null ? "none yet" : s.toFixed(2)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export { LAYER_ORDER, matchLayer, layerScore };
